'use client';

import React, { useState } from 'react';
import LoginForm from './LoginForm';
import SignupForm from './SignupForm';
import RoleModal from './RoleModal';
import { Toaster } from 'sonner';
import Image from 'next/image';

export type AuthMode = 'login' | 'signup';
export type UserRole = 'prospective' | 'browser' | null;

const IVY_SCHOOLS = [
{
  name: 'Harvard',
  color: '#A51C30',
  abbr: 'H',
  logo: 'https://upload.wikimedia.org/wikipedia/commons/thumb/7/70/Harvard_University_logo.svg/200px-Harvard_University_logo.svg.png'
},
{
  name: 'Yale',
  color: '#00356B',
  abbr: 'Y',
  logo: 'https://upload.wikimedia.org/wikipedia/commons/thumb/0/07/Yale_University_Shield_1.svg/200px-Yale_University_Shield_1.svg.png'
},
{
  name: 'Princeton',
  color: '#E77500',
  abbr: 'P',
  logo: 'https://upload.wikimedia.org/wikipedia/commons/thumb/d/d0/Princeton_seal.svg/200px-Princeton_seal.svg.png'
},
{
  name: 'Columbia',
  color: '#003087',
  abbr: 'C',
  logo: 'https://upload.wikimedia.org/wikipedia/commons/thumb/f/f1/Columbia_University_shield.svg/200px-Columbia_University_shield.svg.png'
},
{
  name: 'Penn',
  color: '#011F5B',
  abbr: 'P',
  logo: 'https://upload.wikimedia.org/wikipedia/commons/thumb/9/92/UPenn_shield_with_banner.svg/200px-UPenn_shield_with_banner.svg.png'
},
{
  name: 'Brown',
  color: '#4E3629',
  abbr: 'B',
  logo: 'https://upload.wikimedia.org/wikipedia/commons/thumb/4/47/Brown_University_coat_of_arms.svg/200px-Brown_University_coat_of_arms.svg.png'
},
{
  name: 'Dartmouth',
  color: '#00693E',
  abbr: 'D',
  logo: 'https://upload.wikimedia.org/wikipedia/commons/thumb/8/8e/Dartmouth_College_shield.svg/200px-Dartmouth_College_shield.svg.png'
},
{
  name: 'Cornell',
  color: '#B31B1B',
  abbr: 'C',
  logo: 'https://upload.wikimedia.org/wikipedia/commons/thumb/4/47/Cornell_University_seal.svg/200px-Cornell_University_seal.svg.png'
}];


function IvyLeafIcon({ size = 22, color = 'rgb(59,130,246)' }: {size?: number;color?: string;}) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill={color} xmlns="http://www.w3.org/2000/svg">
      <path d="M12 2C9 2 5 4.5 5 9c0 2.5 1.2 4.5 3 5.8C7.5 17 8 19 8 21h8c0-2 .5-4 .5-4 0 0 0 0 0 0 1.8-1.3 3-3.3 3-5.8 0-4.5-4-7-7.5-7z" />
      <path d="M12 2 L12 21" stroke="rgba(0,0,0,0.15)" strokeWidth="0.5" fill="none" />
      <path d="M12 8 Q9 10 7 13" stroke="rgba(0,0,0,0.12)" strokeWidth="0.5" fill="none" />
      <path d="M12 8 Q15 10 17 13" stroke="rgba(0,0,0,0.12)" strokeWidth="0.5" fill="none" />
    </svg>);

}

export default function AuthPageClient() {
  const [mode, setMode] = useState<AuthMode>('login');
  const [showRoleModal, setShowRoleModal] = useState(false);
  const [userEmail, setUserEmail] = useState('');
  const [imgErrors, setImgErrors] = useState<Record<string, boolean>>({});

  const handleAuthSuccess = (email: string) => {
    setUserEmail(email);
    setShowRoleModal(true);
  };

  const handleImgError = (name: string) => {
    setImgErrors((prev) => ({ ...prev, [name]: true }));
  };

  return (
    <div className="min-h-screen flex">
      <Toaster position="bottom-right" richColors />

      {/* Left Panel — Brand */}
      <div className="hidden lg:flex lg:w-[52%] relative overflow-hidden flex-col justify-between p-12">
        {/* Harvard background photo */}
        <div className="absolute inset-0">
          <Image
            src="https://img.rocket.new/generatedImages/rocket_gen_img_1d89a7e63-1772209033042.png"
            alt="Harvard University campus with historic red brick buildings and lush green lawns"
            fill
            className="object-cover object-center"
            priority
            unoptimized />
          
          {/* Dark overlay for readability */}
          <div className="absolute inset-0 bg-gradient-to-br from-navy/90 via-navy/80 to-navy/70" />
          {/* Blue accent overlay */}
          <div
            className="absolute inset-0 opacity-10"
            style={{
              backgroundImage: `radial-gradient(circle at 20% 20%, rgba(59,130,246,0.6) 0%, transparent 50%),
                radial-gradient(circle at 80% 80%, rgba(96,165,250,0.3) 0%, transparent 50%)`
            }} />
          
        </div>

        {/* Logo */}
        <div className="relative z-10">
          <div className="flex items-center gap-3 mb-12">
            <div className="w-11 h-11 rounded-xl bg-blue-500/20 border border-blue-400/50 flex items-center justify-center shadow-lg">
              <IvyLeafIcon size={24} color="rgb(96,165,250)" />
            </div>
            <div className="flex flex-col leading-none">
              <span className="font-display text-2xl font-bold text-white tracking-wide">IvyLeague</span>
              <span className="text-blue-300 text-sm font-semibold tracking-widest">.app</span>
            </div>
          </div>

          {/* Hero text */}
          <div className="max-w-md">
            <h1 className="font-display text-4xl xl:text-5xl font-bold text-white leading-tight mb-6">
              Your path to the
              <span className="block text-blue-300">Ivy League</span>
              starts here.
            </h1>
            <p className="text-white/75 text-lg leading-relaxed mb-10">
              Explore detailed profiles of all 8 Ivy League universities. Get AI-powered evaluation of your application and discover your best-fit school.
            </p>
          </div>

          {/* Stats row */}
          <div className="grid grid-cols-3 gap-4 mb-10">
            {[
            { value: '8', label: 'Ivy League Schools' },
            { value: '2,400+', label: 'Notable Alumni' },
            { value: '370+', label: 'Nobel Laureates' }].
            map((stat) =>
            <div key={`stat-${stat.label}`} className="border border-white/15 rounded-xl p-4 bg-white/8 backdrop-blur-sm">
                <div className="font-display text-2xl font-bold text-blue-300 tabular-nums">{stat.value}</div>
                <div className="text-white/60 text-sm mt-1">{stat.label}</div>
              </div>
            )}
          </div>
        </div>

        {/* Ivy League School Logos */}
        <div className="relative z-10">
          <p className="text-white/50 text-xs uppercase tracking-widest mb-4 font-sans font-medium">
            The Eight Ivy League Universities
          </p>
          <div className="grid grid-cols-4 gap-3">
            {IVY_SCHOOLS.map((school) =>
            <div
              key={`school-logo-${school.name}`}
              className="flex flex-col items-center gap-2 p-3 rounded-xl bg-white/10 border border-white/15 backdrop-blur-sm hover:bg-white/15 transition-all duration-200">
              
                <div className="w-10 h-10 rounded-lg bg-white flex items-center justify-center overflow-hidden shadow-md">
                  {!imgErrors[school.name] ?
                // eslint-disable-next-line @next/next/no-img-element
                <img
                  src={school.logo}
                  alt={`${school.name} University shield logo`}
                  className="w-8 h-8 object-contain"
                  onError={() => handleImgError(school.name)} /> :


                <div
                  className="w-10 h-10 rounded-lg flex items-center justify-center text-white text-xs font-bold"
                  style={{ backgroundColor: school.color }}>
                  
                      {school.abbr}
                    </div>
                }
                </div>
                <span className="text-white/80 text-xs font-medium text-center leading-tight">{school.name}</span>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Right Panel — Form */}
      <div className="flex-1 flex items-center justify-center p-6 lg:p-12 bg-[#f0f5ff] min-h-screen">
        <div className="w-full max-w-md">
          {/* Mobile logo */}
          <div className="flex items-center gap-2 mb-8 lg:hidden">
            <div className="w-9 h-9 rounded-xl bg-navy flex items-center justify-center">
              <IvyLeafIcon size={20} color="rgb(96,165,250)" />
            </div>
            <div className="flex flex-col leading-none">
              <span className="font-display text-xl font-bold text-navy tracking-wide">IvyLeague</span>
              <span className="text-blue-500 text-xs font-semibold tracking-widest">.app</span>
            </div>
          </div>

          {/* Tab switcher */}
          <div className="flex gap-1 p-1 bg-white rounded-xl border border-blue-100 shadow-sm mb-8">
            {(['login', 'signup'] as AuthMode[]).map((m) =>
            <button
              key={`tab-${m}`}
              onClick={() => setMode(m)}
              className={`flex-1 py-2.5 rounded-lg text-sm font-medium transition-all duration-200 ${
              mode === m ?
              'bg-navy text-white shadow-sm' :
              'text-[#5a6e96] hover:text-navy'}`
              }>
              
                {m === 'login' ? 'Sign In' : 'Create Account'}
              </button>
            )}
          </div>

          {mode === 'login' ?
          <LoginForm onSuccess={handleAuthSuccess} /> :

          <SignupForm onSuccess={handleAuthSuccess} switchToLogin={() => setMode('login')} />
          }

        </div>
      </div>

      {/* Role Selection Modal */}
      {showRoleModal &&
      <RoleModal
        email={userEmail}
        onClose={() => setShowRoleModal(false)} />

      }
    </div>);

}